import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Plus } from 'lucide-react';
import { Money } from '@mlm/ui';
import { getCreatorMe, getStatsOverview, productImageUrl } from '../lib/api';
import { sumSeries, zeroFillDays } from '../lib/series';
import { useHouseMoneyHidden } from '../lib/use-house-money';
import { LmsCard, LmsStat, ViewsChart } from '../components/stats';

const SERIES_DAYS = 30;

/**
 * Creator dashboard: the account status card (the only thing a pending creator
 * sees), then the overview — balance, audience, a 30-day views chart and
 * per-course performance.
 *
 * Every figure comes from the one `/creator/stats/overview` call, so no two
 * blocks here can quote different numbers.
 */
export function DashboardPage() {
  const { data: me } = useQuery({ queryKey: ['creator-me'], queryFn: getCreatorMe });
  const approved = me?.creator.status === 'approved';
  const hideMoney = useHouseMoneyHidden();
  const { data: stats } = useQuery({
    queryKey: ['creator-stats-overview'],
    queryFn: getStatsOverview,
    enabled: approved,
  });

  const series = zeroFillDays(stats?.series ?? [], SERIES_DAYS);
  const totalViews = sumSeries(series, 'views');
  const newEnrollments = sumSeries(series, 'enrollments');
  const enrolled = (stats?.courses ?? []).reduce((a, c) => a + c.enrollments, 0);
  const rating = stats?.audience?.rating;
  const firstName = (me?.creator.displayName ?? '').split(' ')[0] ?? '';

  return (
    <div className="max-w-[1040px] space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-[13px] text-lms-muted">{today()}</div>
          <h1 className="mt-1 font-lms-heading text-[26px] font-bold tracking-[-0.02em] text-lms-ink sm:text-[30px]">
            {greeting()}
            {firstName ? `, ${firstName}` : ''}
          </h1>
        </div>
        {approved && (
          <Link
            to="/courses"
            className="inline-flex h-[42px] items-center gap-2 rounded-full bg-lms px-5 text-sm font-bold text-lms-cream transition-colors hover:bg-lms-dark"
          >
            <Plus className="h-[15px] w-[15px]" strokeWidth={2.4} />
            New course
          </Link>
        )}
      </div>

      {me && me.creator.status !== 'approved' && (
        <LmsCard>
          <div className="p-6">
            <div className="flex items-center gap-2">
              <h2 className="font-lms-heading text-lg font-bold text-lms-ink">
                Application status
              </h2>
              <span className="rounded-full bg-lms-gold-tint px-2.5 py-0.5 text-[11.5px] font-bold capitalize text-lms-gold-deep">
                {me.creator.status}
              </span>
            </div>
            <div className="mt-2 space-y-2 text-sm text-lms-muted">
              {me.creator.status === 'pending' && (
                <p>
                  Your application is being reviewed. You can complete any requirements from your{' '}
                  <Link to="/profile" className="font-semibold text-lms underline">
                    Profile
                  </Link>{' '}
                  page while you wait.
                </p>
              )}
              {(me.creator.status === 'rejected' || me.creator.status === 'suspended') && (
                <p className="text-destructive">
                  {me.creator.status === 'rejected' ? 'Rejected' : 'Suspended'}
                  {me.creator.rejectionReason ? `: ${me.creator.rejectionReason}` : '.'}
                </p>
              )}
            </div>
          </div>
        </LmsCard>
      )}

      {approved && (
        <>
          {/* A HOUSE account earns nothing here (its sales are house revenue),
              so the balance card would always read ₱0.00 — the two audience
              tiles take the whole row instead. */}
          <div
            className={`grid gap-4 ${
              hideMoney ? 'sm:grid-cols-2' : 'md:grid-cols-[1.35fr_1fr_1fr]'
            }`}
          >
            {!hideMoney && (
              <div className="flex min-h-[128px] flex-col justify-between rounded-2xl bg-lms p-6 text-white">
                <div className="flex items-center justify-between gap-2">
                  <div className="text-[13px] font-semibold text-white/75">Available balance</div>
                  <Link
                    to="/payouts"
                    className="text-[12.5px] font-bold text-lms-gold transition-colors hover:text-lms-gold-hover"
                  >
                    Request payout →
                  </Link>
                </div>
                <div>
                  <div className="font-lms-heading text-[34px] font-bold tabular-nums">
                    <Money amount={stats?.wallet.available ?? '0'} direction="neutral" />
                  </div>
                  <div className="mt-0.5 flex items-center gap-1 text-[12.5px] text-white/60">
                    <Money amount={stats?.wallet.lifetimeEarned ?? '0'} direction="neutral" />
                    earned lifetime
                  </div>
                </div>
              </div>
            )}
            <LmsStat
              label="Learners enrolled"
              value={enrolled.toLocaleString()}
              delta={
                newEnrollments > 0 ? `+${newEnrollments.toLocaleString()} in 30 days` : undefined
              }
            />
            <LmsStat
              label="Avg. rating"
              value={rating && rating.count > 0 ? rating.average.toFixed(1) : '—'}
              delta={
                rating && rating.count > 0
                  ? `${rating.count.toLocaleString()} review${rating.count === 1 ? '' : 's'}`
                  : undefined
              }
            />
          </div>

          {/* Audience — the same figures the public instructor page shows.
              Subscribers is omitted entirely when the tenant sells one
              tenant-wide subscription rather than per-creator plans: there is
              no such thing to count, and a zero would read as a failure. */}
          {stats?.audience && (
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
              <LmsStat small label="Followers" value={stats.audience.followers.toLocaleString()} />
              {stats.audience.subscriberCount !== null && (
                <LmsStat
                  small
                  label="Subscribers"
                  value={stats.audience.subscriberCount.toLocaleString()}
                />
              )}
              <LmsStat
                small
                label="Videos published"
                value={stats.audience.videoCount.toLocaleString()}
              />
              <LmsStat small label="Learners reached" value={enrolled.toLocaleString()} />
            </div>
          )}

          <LmsCard>
            <div className="flex items-center justify-between px-6 pb-1 pt-5">
              <h2 className="font-lms-heading text-lg font-bold text-lms-ink">
                Views · last 30 days
              </h2>
              <div className="text-[12.5px] text-lms-muted">
                {totalViews.toLocaleString()} total
              </div>
            </div>
            <div className="px-6 pb-6 pt-3">
              <ViewsChart points={series} />
            </div>
          </LmsCard>

          <LmsCard>
            <div className="flex items-center justify-between px-6 pb-3 pt-5">
              <h2 className="font-lms-heading text-lg font-bold text-lms-ink">
                Course performance
              </h2>
              <Link to="/courses" className="text-[13px] font-bold text-lms hover:text-lms-dark">
                Manage courses →
              </Link>
            </div>
            <div className="px-3 pb-3">
              {!stats || stats.courses.length === 0 ? (
                <p className="py-8 text-center text-sm text-lms-muted">
                  No courses yet —{' '}
                  <Link to="/courses" className="font-semibold text-lms underline">
                    create your first course
                  </Link>
                  .
                </p>
              ) : (
                stats.courses.map((c) => (
                  <Link
                    key={c.courseId}
                    to={`/courses/${c.courseId}/stats`}
                    className="grid items-center gap-3.5 rounded-xl px-3 py-2.5 transition-colors hover:bg-lms-paper md:grid-cols-[minmax(0,2.2fr)_repeat(3,minmax(0,0.8fr))_minmax(0,1.4fr)]"
                  >
                    <div className="flex min-w-0 items-center gap-3">
                      <img
                        src={productImageUrl(c.productId, 0)}
                        alt=""
                        loading="lazy"
                        className="h-10 w-16 flex-none rounded-lg bg-lms-light object-cover"
                        onError={(e) => {
                          (e.currentTarget as HTMLImageElement).style.visibility = 'hidden';
                        }}
                      />
                      <div className="min-w-0">
                        <div className="truncate text-sm font-bold text-lms-ink">{c.title}</div>
                        <div className="text-[11.5px] capitalize text-lms-muted">{c.status}</div>
                      </div>
                    </div>
                    <Figure label="Enrolled" value={c.enrollments.toLocaleString()} />
                    <Figure label="Views" value={c.views.toLocaleString()} />
                    <Figure
                      label="Rating"
                      value={c.ratingAvg != null ? c.ratingAvg.toFixed(1) : '—'}
                      gold
                    />
                    <div>
                      <div className="flex justify-between text-[11px] text-lms-muted">
                        <span>Completion</span>
                        <span className="font-bold text-lms">{c.completionPct}%</span>
                      </div>
                      <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-lms-light">
                        <div
                          className="h-full rounded-full bg-lms"
                          style={{ width: `${c.completionPct}%` }}
                        />
                      </div>
                    </div>
                  </Link>
                ))
              )}
            </div>
          </LmsCard>
        </>
      )}
    </div>
  );
}

function Figure({ label, value, gold }: { label: string; value: string; gold?: boolean }) {
  return (
    <div>
      <div className="text-[11px] text-lms-muted">{label}</div>
      <div
        className={`text-[14.5px] font-bold tabular-nums ${gold ? 'text-lms-gold-deep' : 'text-lms-ink'}`}
      >
        {value}
      </div>
    </div>
  );
}

function greeting(now = new Date()) {
  const h = now.getHours();
  if (h < 12) return 'Good morning';
  if (h < 18) return 'Good afternoon';
  return 'Good evening';
}

function today(now = new Date()) {
  return now.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' });
}
